import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import notificationService from '../../services/api/notificationService';

export const NOTIFICATIONS_KEY = ['notifications'];
export const NOTIFICATION_COUNT_KEY = ['notifications', 'count'];

/**
 * Hook to fetch notifications
 */
export function useNotifications(options = {}) {
  return useQuery({
    queryKey: [...NOTIFICATIONS_KEY, options],
    queryFn: () => notificationService.getNotifications(options),
    staleTime: 1000 * 30, // 30 seconds
    refetchOnWindowFocus: true
  });
}

/**
 * Hook to fetch unread notification count
 */
export function useUnreadCount() {
  return useQuery({
    queryKey: NOTIFICATION_COUNT_KEY,
    queryFn: () => notificationService.getUnreadCount(),
    staleTime: 1000 * 30, // 30 seconds
    refetchInterval: 1000 * 60 // Refetch every minute
  });
}

/**
 * Hook to fetch a single notification
 */
export function useNotification(id) {
  return useQuery({
    queryKey: [...NOTIFICATIONS_KEY, id],
    queryFn: () => notificationService.getNotification(id),
    enabled: !!id,
    staleTime: 1000 * 60
  });
}

/**
 * Hook to mark a notification as read
 */
export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => notificationService.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_COUNT_KEY });
    }
  });
}

/**
 * Hook to mark all notifications as read
 */
export function useMarkAllAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_COUNT_KEY });
    }
  });
}

/**
 * Hook to dismiss a notification
 */
export function useDismissNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => notificationService.dismiss(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_COUNT_KEY });
    }
  });
}

export default {
  useNotifications,
  useUnreadCount,
  useNotification,
  useMarkAsRead,
  useMarkAllAsRead,
  useDismissNotification
};
